import React, { createContext, useContext, useMemo, useEffect } from 'react'
import { useStatusOverlay } from '@fs/zion-ui'
import { useTranslation } from 'react-i18next'
import useDynamicNavigation from './useDynamicNavigation'

const NavigationContext = createContext()

export const useNavigationContext = () => {
  const context = useContext(NavigationContext)
  if (!context) {
    throw new Error('useNavigationContext must be used within a NavigationProvider')
  }
  return context
}

export default function NavigationProvider({ children }) {
  const { headerNavigation, accountNavigation, helpNavigation, isLoading, isError } = useDynamicNavigation()
  const showStatusOverlay = useStatusOverlay()
  const { t } = useTranslation()

  const dynamicNavigationReady = headerNavigation && !isLoading

  useEffect(() => {
    if (isError) {
      showStatusOverlay({
        message: t('header.dynamic-links-error-msg-refresh', 'Please try again in a moment or refresh the page.'),
        type: 'error',
      })
    }
  }, [isError, showStatusOverlay, t])

  const value = useMemo(
    () => ({
      headerNavigation,
      accountNavigation,
      helpNavigation,
      isLoading: !dynamicNavigationReady,
      isError,
    }),
    [headerNavigation, accountNavigation, helpNavigation, dynamicNavigationReady, isError]
  )

  return <NavigationContext.Provider value={value}>{children}</NavigationContext.Provider>
}
